import { useMovieStore, useUIStore } from '../stores';
import type { MovieSummary } from '../types';
import { authFetch } from './authFetch';
import { handleUnauthorizedRedirect } from './authRedirect';

const DEFAULT_WIDTH = 800;
const DEFAULT_HEIGHT = 600;
const DEFAULT_FPS = 24;
const MOVIE_READY_POLL_MS = 100;
const MOVIE_READY_TIMEOUT_MS = 60_000;

type CreateMovieResponse = { id: number; message?: string };

/** Movie seçili ve editör store'unda yüklü mü? */
export function isMovieReadyForChat(): boolean {
  const { selectedMovieId, currentMovie } = useMovieStore.getState();
  if (!selectedMovieId) return false;
  if (!currentMovie) return false;
  return String(currentMovie.id) === String(selectedMovieId);
}

/** Chat göndermeden önce movie yaratmak veya yüklenmesini beklemek gerekir mi? */
export function needsMovieForChat(): boolean {
  return !isMovieReadyForChat();
}

/** İlk kullanıcı mesajından proje adı türet (client-side heuristic). */
export function deriveMovieNameFromPrompt(message: string): string {
  const trimmed = message.trim();
  if (!trimmed) return 'Untitled Project';

  const firstLine = trimmed.split(/[\n.!?]/)[0]?.trim() ?? trimmed;
  if (firstLine.length <= 60) return firstLine;
  return `${firstLine.slice(0, 57)}...`;
}

async function createMovieViaApi(name: string): Promise<number | null> {
  const response = await authFetch('/api/movies/create', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      width: DEFAULT_WIDTH,
      height: DEFAULT_HEIGHT,
      fps: DEFAULT_FPS,
    }),
  });

  if (response.status === 401) {
    handleUnauthorizedRedirect();
    return null;
  }

  if (!response.ok) {
    console.error('[ensureMovieForChat] create failed', response.status, await response.text());
    return null;
  }

  const data = (await response.json()) as CreateMovieResponse;
  return typeof data.id === 'number' ? data.id : null;
}

function registerCreatedMovie(movieId: number, name: string): void {
  const { userAllMovies, setUserAllMovies, setSelectedMovieId } = useMovieStore.getState();
  useUIStore.getState().setWelcomeOpen(false);
  setSelectedMovieId(movieId);

  const summary: MovieSummary = {
    id: movieId,
    name,
    description: '',
    width: DEFAULT_WIDTH,
    height: DEFAULT_HEIGHT,
    fps: DEFAULT_FPS,
    isSample: false,
  };

  if (!userAllMovies.some((movie) => movie.id === movieId)) {
    setUserAllMovies([summary, ...userAllMovies]);
  }
}

export async function waitForMovieReady(
  movieId: number,
  timeoutMs = MOVIE_READY_TIMEOUT_MS
): Promise<boolean> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const { selectedMovieId, currentMovie } = useMovieStore.getState();
    if (
      selectedMovieId === movieId &&
      currentMovie &&
      String(currentMovie.id) === String(movieId)
    ) {
      return true;
    }
    await new Promise((resolve) => setTimeout(resolve, MOVIE_READY_POLL_MS));
  }
  return false;
}

/**
 * Movie yoksa otomatik yaratır ve editörde açılmasını bekler.
 * Movie seçili ama henüz yüklenmemişse yüklenene kadar bekler.
 */
export async function ensureMovieForChat(prompt: string): Promise<number | null> {
  if (isMovieReadyForChat()) {
    return useMovieStore.getState().selectedMovieId;
  }

  const { selectedMovieId } = useMovieStore.getState();
  if (selectedMovieId) {
    const ready = await waitForMovieReady(selectedMovieId);
    return ready ? selectedMovieId : null;
  }

  const name = deriveMovieNameFromPrompt(prompt);
  const movieId = await createMovieViaApi(name);
  if (!movieId) return null;

  registerCreatedMovie(movieId, name);
  const ready = await waitForMovieReady(movieId);
  return ready ? movieId : null;
}
